import { GRAPHQL_ENDPOINT } from './client'
import { getAccessToken } from './graphqlAuth'

type GraphqlMultipartResponse<T> = {
  data?: T
  errors?: Array<{ message: string }>
}

/**
 * Error message thrown on a transport-level failure (non-OK HTTP, or a 2xx
 * response with no data) — as opposed to a GraphQL error, which throws the
 * server's message. It is a sentinel, not user copy: surface a translated
 * message instead of showing it verbatim.
 */
export const MULTIPART_REQUEST_FAILED = 'multipartRequestFailed'

/**
 * POST a GraphQL operation carrying a single file variable via the
 * graphql-multipart-request-spec. The file travels as FormData (so it can't go
 * through `graphqlClient.request`): `variables.file` is nulled in `operations`
 * and mapped to the uploaded part. Returns the operation's `data` payload.
 *
 * @see https://github.com/jaydenseric/graphql-multipart-request-spec
 */
export async function postGraphqlMultipart<T>(
  query: string,
  variables: Record<string, unknown>,
  file: File,
): Promise<T> {
  const formData = new FormData()
  formData.append(
    'operations',
    JSON.stringify({ query, variables: { ...variables, file: null } }),
  )
  formData.append('map', JSON.stringify({ '0': ['variables.file'] }))
  formData.append('0', file)

  const headers: HeadersInit = {}
  const token = getAccessToken()
  if (token) headers['Authorization'] = `Bearer ${token}`

  const response = await fetch(GRAPHQL_ENDPOINT, {
    method: 'POST',
    body: formData,
    headers,
  })
  if (!response.ok) throw new Error(MULTIPART_REQUEST_FAILED)

  const result: GraphqlMultipartResponse<T> = await response.json()
  if (result.errors) {
    throw new Error(result.errors.map((error) => error.message).join(', '))
  }
  if (!result.data) throw new Error(MULTIPART_REQUEST_FAILED)
  return result.data
}
